import { useCallback, useMemo } from 'react';

import { routes } from '~constants/routes';
import type { PaymentType } from '~context/paywall/types';
import { sendEvent } from '~features/annals';
import { useUserId } from '~hooks/useUserId';
import { usePathname } from '~i18n/navigation';
import type { TMotoEvent } from '~shared/services/logger/shared';
import { convertToBackendId } from '~utils/ids';

import { events, type PaymentReason, PaymentReasonType } from '../names';
import { useSendEvent } from '../useSendEvent';

export const usePaymentReasonType = () => {
	const pathname = usePathname();
	const reasonType = useMemo(() => {
		if (
			pathname === routes.explore.home ||
			pathname.startsWith(routes.explore.models)
		) {
			return PaymentReasonType.Grid;
		}

		if (pathname === routes.chats.home) {
			return PaymentReasonType.Chats;
		}

		if (pathname.startsWith(routes.chats.home)) {
			return PaymentReasonType.Chat;
		}

		if (pathname === routes.generate.character) {
			return PaymentReasonType.GToolBot;
		}

		if (
			pathname === routes.generate.images ||
			pathname === routes.generate.videos
		) {
			return PaymentReasonType.GToolMedia;
		}

		return PaymentReasonType.Unknown;
	}, [pathname]);

	return reasonType;
};

export const useOpenStore = () =>
	useSendEvent<{
		reason?: PaymentReason;
		reasonType?: PaymentReasonType;
		bot_UserId?: number | string;
		bot_name?: string;
		type: PaymentType;
	}>(events.checkout.openStore);

export const usePaymentOpenClicked = ({
	reason,
	botClientId,
	type,
	reasonType,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			bot_UserId: botClientId,
			type,
			reasonType,
		}),
		[type, botClientId, reason, reasonType]
	);

	return useSendEvent(events.checkout.paymentOpenClick, params);
};

export const useAnnalsOnProductSelected = ({
	reason,
	botClientId,
	type,
	reasonType,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			bot_UserId: botClientId,
			type,
			reasonType,
		}),
		[type, botClientId, reason, reasonType]
	);

	return useSendEvent(events.checkout.paymentOnSelectPack, params);
};

export const useBeginCheckout = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useSendEvent(events.checkout.beginCheckout, params);
};

export const usePromocodeApplied = ({
	reason,
	reasonType,
	type,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			reasonType,
			type,
		}),
		[reason, reasonType, type]
	);

	return useSendEvent(events.checkout.promocodeApplied, params);
};

export const useMotoEventProxy = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const userId = useUserId();
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useCallback(
		async ({
			payload,
			type,
		}: {
			payload?: Record<string, unknown>;
			type: TMotoEvent;
		}) => {
			return sendEvent({
				userId,
				event: type,
				body: {
					...params,
					...payload,
					bot_UserId: params.bot_UserId
						? convertToBackendId(params.bot_UserId as string)
						: undefined,
					timestamp: (Date.now() / 1000) | 0,
				},
			});
		},
		[params, userId]
	);
};

// TODO: should be removed, see note for OpenStoreDigitalCopy
export const useOpenDigitalCopySubscription = () =>
	useSendEvent<{
		bot_UserId?: number | string;
		bot_name?: string;
	}>(events.checkout.openDigitalCopySubscription);

// TODO: should be removed, see note for BeginCheckoutDigitalCopy
export const useBeginCheckoutDigitalCopy = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useSendEvent(events.checkout.beginCheckoutDigitalCopy, params);
};
